import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CONDITIONS } from "@/data/conditions";
import { SEO } from "@/components/Seo";

export default function ScienceHub() {
  return (
    <div data-testid="science-hub-page">
      <SEO
        title="The Science Hub — Six Conditions, Zero Jargon | Pranivaa Kitchens"
        description="What clean plant-forward eating actually does in your body. Plain-language guides for diabetes, PCOS, thyroid, gut health, heart health and weight."
        path="/science"
      />
      <section className="pt-28 md:pt-40 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-5">
              The Science Hub
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink font-semibold max-w-3xl mb-6">
              What clean plant-forward eating actually does in your body.
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-ink-soft max-w-2xl">
              Each guide explains one condition in plain language — the mechanism, the Indian foods
              that support its management, and what to be thoughtful about. Educational content, not
              medical advice.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CONDITIONS.map((c, i) => (
              <StaggerItem key={c.slug}>
                <Link
                  to={`/science/${c.slug}`}
                  data-testid={`science-card-${c.slug}`}
                  className="group flex gap-6 bg-paper-card border border-[#E7E2D7] rounded-2xl overflow-hidden h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-clay/40"
                >
                  <div className="w-32 sm:w-44 shrink-0 overflow-hidden">
                    <img loading="lazy" decoding="async"
                      src={c.image}
                      alt={c.imageAlt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="py-6 pr-6 flex flex-col">
                    <span className="font-mono text-[11px] tracking-[0.15em] uppercase font-semibold text-clay mb-2">
                      {String(i + 1).padStart(2, "0")} — {c.navName}
                    </span>
                    <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink leading-snug mb-2">
                      {c.title}
                    </h2>
                    <p className="text-sm text-ink-soft leading-relaxed mb-4">{c.tagline}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-bold tracking-wide text-beet uppercase">
                      Read the guide
                      <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-12">
            <p className="text-xs font-mono text-ink-muted max-w-2xl leading-relaxed">
              Compliance note: In line with FSSAI advertising norms, nothing on these pages claims to
              treat, cure or prevent disease. Food supports management — alongside your doctor, your
              movement and your sleep.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
